// ── Helpers ──
export function getStartNode(graph) {
    const node = graph.nodes.find((n) => n.type === 'start');
    return node ?? null;
}
export function getNodeById(graph, id) {
    return graph.nodes.find((n) => n.id === id) ?? null;
}
export function getMaxActors(graph) {
    const inviteNode = graph.nodes.find((n) => n.type === 'inviteActors');
    if (!inviteNode)
        return 1;
    return inviteNode.data.maxActors ?? 1;
}
export function getActorLabel(graph) {
    const inviteNode = graph.nodes.find((n) => n.type === 'inviteActors');
    if (!inviteNode)
        return 'Participante';
    return inviteNode.data.actorLabel?.trim() || 'Participante';
}
export function getOutgoingEdges(graph, nodeId) {
    return graph.edges.filter((e) => e.source === nodeId);
}
export function getIncomingEdges(graph, nodeId) {
    return graph.edges.filter((e) => e.target === nodeId);
}
export function findReachableNodes(graph, fromNodeId) {
    const visited = new Set();
    const stack = [fromNodeId];
    while (stack.length > 0) {
        const current = stack.pop();
        if (visited.has(current))
            continue;
        visited.add(current);
        for (const edge of getOutgoingEdges(graph, current)) {
            if (!visited.has(edge.target))
                stack.push(edge.target);
        }
    }
    return visited;
}
/**
 * Returns all simple paths (no repeated nodes) from `fromId` to any `end` node.
 */
export function findAllPathsToEnds(graph, fromId) {
    const paths = [];
    const walk = (nodeId, currentPath) => {
        if (currentPath.includes(nodeId))
            return;
        const path = [...currentPath, nodeId];
        const node = getNodeById(graph, nodeId);
        if (!node)
            return;
        if (node.type === 'end') {
            paths.push(path);
            return;
        }
        const outgoing = getOutgoingEdges(graph, nodeId);
        if (outgoing.length === 0) {
            paths.push(path);
            return;
        }
        for (const edge of outgoing) {
            walk(edge.target, path);
        }
    };
    walk(fromId, []);
    return paths;
}
/**
 * Returns the set of field keys collected by userForm (and precondition) nodes
 * that precede `nodeId` on every path from `start` to `nodeId`.
 *
 * A field is "guaranteed available" only if every path into nodeId collects it.
 */
export function computeAvailableFieldsAt(graph, nodeId) {
    const start = getStartNode(graph);
    if (!start)
        return [];
    if (start.id === nodeId)
        return [];
    const paths = findAllPathsToEnds(graph, start.id)
        .filter((p) => p.includes(nodeId))
        .map((p) => p.slice(0, p.indexOf(nodeId)));
    if (paths.length === 0)
        return [];
    const perPathFields = [];
    for (const path of paths) {
        const map = new Map();
        for (const id of path) {
            const node = getNodeById(graph, id);
            if (!node)
                continue;
            if (node.type === 'userForm') {
                const data = node.data;
                for (const f of data.fields ?? []) {
                    map.set(f.key, { label: f.label, type: f.type, options: f.options ? [...f.options] : undefined });
                }
            }
            else if (node.type === 'precondition') {
                const data = node.data;
                const actorCount = getMaxActors(graph);
                let hasPerActorFields = false;
                for (const f of data.fields ?? []) {
                    if (f.perActor && actorCount > 1) {
                        hasPerActorFields = true;
                        for (let a = 0; a < actorCount; a++) {
                            map.set(`${f.key}_${a}`, { label: f.label, type: f.type, options: f.options ? [...f.options] : undefined });
                        }
                    }
                    else {
                        map.set(f.key, { label: f.label, type: f.type, options: f.options ? [...f.options] : undefined });
                    }
                }
                // Expose __actorCount as a synthetic field when per-actor fields exist
                if (hasPerActorFields) {
                    map.set('__actorCount', { label: 'Actor count', type: 'number' });
                }
            }
        }
        perPathFields.push(map);
    }
    const first = perPathFields[0];
    const guaranteed = [];
    for (const [key, meta] of first.entries()) {
        if (perPathFields.every((m) => m.has(key))) {
            guaranteed.push({ key, ...meta });
        }
    }
    return guaranteed;
}
export function validateGraph(graph) {
    const errors = [];
    const startNodes = graph.nodes.filter((n) => n.type === 'start');
    if (startNodes.length === 0) {
        errors.push({ code: 'NO_START', message: 'Graph must have exactly one start node' });
    }
    else if (startNodes.length > 1) {
        errors.push({ code: 'MULTIPLE_START', message: 'Graph must have exactly one start node' });
    }
    const endNodes = graph.nodes.filter((n) => n.type === 'end');
    if (endNodes.length === 0) {
        errors.push({ code: 'NO_END', message: 'Graph must have at least one end node' });
    }
    const nodeIds = new Set(graph.nodes.map((n) => n.id));
    for (const edge of graph.edges) {
        if (!nodeIds.has(edge.source)) {
            errors.push({ code: 'EDGE_BAD_SOURCE', message: `Edge source "${edge.source}" not found`, edgeId: edge.id });
        }
        if (!nodeIds.has(edge.target)) {
            errors.push({ code: 'EDGE_BAD_TARGET', message: `Edge target "${edge.target}" not found`, edgeId: edge.id });
        }
    }
    const start = startNodes[0];
    if (start) {
        const reachable = findReachableNodes(graph, start.id);
        for (const node of graph.nodes) {
            if (!reachable.has(node.id)) {
                errors.push({
                    code: 'UNREACHABLE',
                    message: `Node "${node.id}" is not reachable from start`,
                    nodeId: node.id,
                });
            }
        }
        if (hasCycle(graph, start.id)) {
            errors.push({ code: 'CYCLE_DETECTED', message: 'Graph contains a cycle' });
        }
    }
    for (const node of graph.nodes) {
        const outgoing = getOutgoingEdges(graph, node.id);
        if (node.type === 'end') {
            if (outgoing.length > 0) {
                errors.push({
                    code: 'END_HAS_OUTGOING',
                    message: 'End node must have no outgoing edges',
                    nodeId: node.id,
                });
            }
            continue;
        }
        if (outgoing.length === 0) {
            errors.push({
                code: 'NODE_NO_OUTGOING',
                message: `Node has no outgoing edge`,
                nodeId: node.id,
            });
        }
        if (node.type === 'branch') {
            if (outgoing.length < 2) {
                errors.push({
                    code: 'BRANCH_TOO_FEW_OUTGOING',
                    message: 'Branch node must have at least two outgoing edges',
                    nodeId: node.id,
                });
            }
            const data = node.data;
            if (!data.field) {
                errors.push({
                    code: 'BRANCH_NO_FIELD',
                    message: 'Branch node must reference a field',
                    nodeId: node.id,
                });
            }
            else if (start) {
                const available = computeAvailableFieldsAt(graph, node.id).map((f) => f.key);
                if (!available.includes(data.field)) {
                    errors.push({
                        code: 'BRANCH_FIELD_UNAVAILABLE',
                        message: `Branch field "${data.field}" is not available on every path leading to this node`,
                        nodeId: node.id,
                    });
                }
            }
            const cases = data.cases ?? [];
            if (cases.length === 0) {
                errors.push({
                    code: 'BRANCH_NO_CASES',
                    message: 'Branch node must define at least one case',
                    nodeId: node.id,
                });
            }
            const hasDefault = outgoing.some((e) => e.sourceHandle === 'default' || e.data?.isDefault === true);
            if (!hasDefault) {
                for (const c of cases) {
                    const wired = outgoing.some((e) => e.sourceHandle === c.id || e.data?.caseId === c.id);
                    if (!wired) {
                        errors.push({
                            code: 'BRANCH_CASE_UNWIRED',
                            message: `Branch case "${c.label?.trim() || c.id}" has no outgoing edge and there is no default edge`,
                            nodeId: node.id,
                        });
                    }
                }
            }
        }
        if (node.type === 'precondition') {
            const data = node.data;
            const actorCount = getMaxActors(graph);
            const fieldKeys = new Set();
            for (const f of data.fields ?? []) {
                fieldKeys.add(f.key);
                if (f.perActor && actorCount > 1) {
                    for (let a = 0; a < actorCount; a++) {
                        fieldKeys.add(`${f.key}_${a}`);
                    }
                }
            }
            for (const rule of data.rules ?? []) {
                if (rule.type === 'max_percentage') {
                    for (const sf of rule.params.sumFields) {
                        if (!fieldKeys.has(sf)) {
                            errors.push({
                                code: 'PRECONDITION_FIELD_MISSING',
                                message: `Precondition sumField "${sf}" not in precondition fields`,
                                nodeId: node.id,
                            });
                        }
                    }
                    if (!fieldKeys.has(rule.params.referenceField)) {
                        errors.push({
                            code: 'PRECONDITION_FIELD_MISSING',
                            message: `Precondition referenceField "${rule.params.referenceField}" not in precondition fields`,
                            nodeId: node.id,
                        });
                    }
                }
                else if (rule.type === 'conditional') {
                    for (const cond of rule.conditions ?? []) {
                        for (const fieldRef of collectFieldRefs(cond)) {
                            if (!fieldKeys.has(fieldRef)) {
                                errors.push({
                                    code: 'PRECONDITION_FIELD_MISSING',
                                    message: `Precondition condition references field "${fieldRef}" which is not in precondition fields`,
                                    nodeId: node.id,
                                });
                            }
                        }
                    }
                }
            }
            const passEdge = outgoing.find((e) => e.sourceHandle === 'pass');
            if (!passEdge) {
                errors.push({
                    code: 'PRECONDITION_NO_PASS_EDGE',
                    message: 'Precondition node must have an outgoing edge with sourceHandle "pass"',
                    nodeId: node.id,
                });
            }
        }
        if (node.type === 'internalRequest') {
            const d = node.data;
            if (!d.label) {
                errors.push({ code: 'INTERNAL_REQUEST_NO_LABEL', nodeId: node.id, message: 'internalRequest node must have a label' });
            }
            if (d.requestType !== 'platform_request') {
                errors.push({ code: 'INTERNAL_REQUEST_BAD_TYPE', nodeId: node.id, message: 'internalRequest requestType must be "platform_request"' });
            }
            if (!d.productFilter?.flowKey) {
                errors.push({ code: 'INTERNAL_REQUEST_NO_FLOW_KEY', nodeId: node.id, message: 'internalRequest must have productFilter.flowKey' });
            }
            const outEdges = getOutgoingEdges(graph, node.id);
            if (outEdges.length !== 1) {
                errors.push({ code: 'INTERNAL_REQUEST_BAD_OUTGOING', nodeId: node.id, message: `internalRequest must have exactly 1 outgoing edge, found ${outEdges.length}` });
            }
        }
        if (node.type === 'documentCollection') {
            const d = node.data;
            if (!d.label?.trim()) {
                errors.push({ code: 'DOC_COLLECTION_NO_LABEL', nodeId: node.id, message: 'documentCollection node must have a label' });
            }
            if (!d.conditionField?.trim()) {
                errors.push({ code: 'DOC_COLLECTION_NO_CONDITION_FIELD', nodeId: node.id, message: 'documentCollection must have a conditionField' });
            }
            const sets = Object.values(d.documentSets ?? {});
            if (sets.length === 0) {
                errors.push({ code: 'DOC_COLLECTION_NO_SETS', nodeId: node.id, message: 'documentCollection must have at least one document set' });
            }
            for (const [key, set] of Object.entries(d.documentSets ?? {})) {
                if (!set.documents?.length) {
                    errors.push({ code: 'DOC_COLLECTION_EMPTY_SET', nodeId: node.id, message: `Document set "${key}" has no documents` });
                }
            }
            const outEdges = getOutgoingEdges(graph, node.id);
            if (outEdges.length !== 1) {
                errors.push({ code: 'DOC_COLLECTION_BAD_OUTGOING', nodeId: node.id, message: `documentCollection must have exactly 1 outgoing edge, found ${outEdges.length}` });
            }
        }
        if (node.type === 'inviteActors') {
            const d = node.data;
            if (!d.label || d.label.trim().length === 0) {
                errors.push({ code: 'INVITE_ACTORS_NO_LABEL', nodeId: node.id, message: 'inviteActors node must have a label' });
            }
            if (!d.maxActors || d.maxActors < 2 || d.maxActors > 10) {
                errors.push({ code: 'INVITE_ACTORS_BAD_MAX', nodeId: node.id, message: 'maxActors must be between 2 and 10' });
            }
            const outEdges = getOutgoingEdges(graph, node.id);
            if (outEdges.length !== 1) {
                errors.push({ code: 'INVITE_ACTORS_BAD_OUTGOING', nodeId: node.id, message: `inviteActors must have exactly 1 outgoing edge, found ${outEdges.length}` });
            }
        }
        if (node.type === 'createRequest') {
            const d = node.data;
            if (!d.label || d.label.trim().length === 0) {
                errors.push({ code: 'CREATE_REQUEST_NO_LABEL', nodeId: node.id, message: 'createRequest node must have a label' });
            }
            if (outgoing.length !== 1) {
                errors.push({ code: 'CREATE_REQUEST_BAD_OUTGOING', nodeId: node.id, message: `createRequest must have exactly 1 outgoing edge, found ${outgoing.length}` });
            }
        }
        if (node.type === 'userForm') {
            const d = node.data;
            if (d.actorSource) {
                const sourceNode = getNodeById(graph, d.actorSource);
                if (!sourceNode) {
                    errors.push({
                        code: 'USER_FORM_ACTOR_SOURCE_MISSING',
                        nodeId: node.id,
                        message: `actorSource references node "${d.actorSource}" which does not exist`,
                    });
                }
                else if (sourceNode.type !== 'inviteActors') {
                    errors.push({
                        code: 'USER_FORM_ACTOR_SOURCE_NOT_INVITE',
                        nodeId: node.id,
                        message: `actorSource must reference an inviteActors node, got "${sourceNode.type}"`,
                    });
                }
                else if (start) {
                    const paths = findAllPathsToEnds(graph, start.id)
                        .filter((p) => p.includes(node.id))
                        .map((p) => p.slice(0, p.indexOf(node.id)));
                    const anyPathIncludes = paths.some((p) => p.includes(d.actorSource));
                    if (!anyPathIncludes) {
                        errors.push({
                            code: 'USER_FORM_ACTOR_SOURCE_NOT_UPSTREAM',
                            nodeId: node.id,
                            message: `actorSource node "${d.actorSource}" is not upstream on any path leading to this node`,
                        });
                    }
                }
            }
        }
        if (node.type === 'decision') {
            const d = node.data;
            if (!d.label || d.label.trim().length === 0) {
                errors.push({ code: 'DECISION_NO_LABEL', nodeId: node.id, message: 'Decision node must have a label' });
            }
            if (!d.outputKey || d.outputKey.trim().length === 0) {
                errors.push({ code: 'DECISION_NO_OUTPUT_KEY', nodeId: node.id, message: 'Decision node must have an outputKey' });
            }
            if (!d.options || d.options.length < 2) {
                errors.push({ code: 'DECISION_TOO_FEW_OPTIONS', nodeId: node.id, message: 'Decision node must have at least 2 options' });
            }
            if (d.displayFrom && start) {
                const available = computeAvailableFieldsAt(graph, node.id);
                const displayNode = getNodeById(graph, d.displayFrom);
                if (!displayNode) {
                    errors.push({ code: 'DECISION_DISPLAY_NODE_MISSING', nodeId: node.id, message: `displayFrom node "${d.displayFrom}" not found` });
                }
                else {
                    // Ensure displayFrom node is on every path leading to this node
                    const paths = findAllPathsToEnds(graph, start.id)
                        .filter((p) => p.includes(node.id))
                        .map((p) => p.slice(0, p.indexOf(node.id)));
                    const allPathsInclude = paths.every((p) => p.includes(d.displayFrom));
                    if (!allPathsInclude) {
                        errors.push({
                            code: 'DECISION_DISPLAY_NODE_UNREACHABLE',
                            nodeId: node.id,
                            message: `displayFrom node "${d.displayFrom}" is not on every path leading to this decision node`,
                        });
                    }
                }
            }
            // Validate option edges: each option must have a wired edge OR a default edge exists
            if (d.options && d.options.length > 0) {
                const hasDefault = outgoing.some((e) => e.sourceHandle === 'default' || e.data?.isDefault === true);
                if (!hasDefault) {
                    for (const opt of d.options) {
                        const wired = outgoing.some((e) => e.sourceHandle === opt.value);
                        if (!wired) {
                            errors.push({
                                code: 'DECISION_OPTION_UNWIRED',
                                nodeId: node.id,
                                message: `Decision option "${opt.label || opt.value}" has no outgoing edge and there is no default edge`,
                            });
                        }
                    }
                }
            }
        }
        if (node.type === 'pricing' && start) {
            const data = node.data;
            const available = new Set(computeAvailableFieldsAt(graph, node.id).map((f) => f.key));
            for (const bf of data.formula.baseFields) {
                if (!available.has(bf)) {
                    errors.push({
                        code: 'PRICING_FIELD_UNAVAILABLE',
                        message: `Pricing baseField "${bf}" is not available on every path leading to this pricing node`,
                        nodeId: node.id,
                    });
                }
            }
            if (!available.has(data.formula.profileField)) {
                errors.push({
                    code: 'PRICING_FIELD_UNAVAILABLE',
                    message: `Pricing profileField "${data.formula.profileField}" is not available on every path leading to this pricing node`,
                    nodeId: node.id,
                });
            }
            if (data.formula.installmentField && !available.has(data.formula.installmentField)) {
                errors.push({
                    code: 'PRICING_FIELD_UNAVAILABLE',
                    message: `Pricing installmentField "${data.formula.installmentField}" is not available on every path leading to this pricing node`,
                    nodeId: node.id,
                });
            }
        }
    }
    const allFieldKeys = new Map();
    for (const node of graph.nodes) {
        if (node.type === 'userForm') {
            const data = node.data;
            for (const f of data.fields ?? []) {
                if (allFieldKeys.has(f.key)) {
                    errors.push({
                        code: 'FIELD_KEY_DUPLICATE',
                        message: `Field key "${f.key}" is used in multiple nodes`,
                        nodeId: node.id,
                    });
                }
                else {
                    allFieldKeys.set(f.key, node.id);
                }
            }
        }
        if (node.type === 'precondition') {
            const data = node.data;
            for (const f of data.fields ?? []) {
                if (allFieldKeys.has(f.key)) {
                    errors.push({
                        code: 'FIELD_KEY_DUPLICATE',
                        message: `Field key "${f.key}" is used in multiple nodes`,
                        nodeId: node.id,
                    });
                }
                else {
                    allFieldKeys.set(f.key, node.id);
                }
            }
        }
    }
    if (start) {
        const paths = findAllPathsToEnds(graph, start.id);
        for (const path of paths) {
            const pricingCount = path.filter((id) => {
                const node = getNodeById(graph, id);
                return node?.type === 'pricing';
            }).length;
            if (pricingCount > 1) {
                errors.push({
                    code: 'MULTIPLE_PRICING_ON_PATH',
                    message: 'A path from start to end contains more than one pricing node',
                });
                break;
            }
        }
    }
    return errors;
}
function collectExprFieldRefs(expr, acc) {
    switch (expr.kind) {
        case 'field':
            acc.push(expr.key);
            return;
        case 'sum':
            for (const k of expr.fields)
                acc.push(k);
            return;
        case 'product':
            for (const e of expr.factors)
                collectExprFieldRefs(e, acc);
            return;
        case 'percentageOf':
            acc.push(expr.field);
            return;
        case 'const':
            return;
    }
}
function collectFieldRefs(cond) {
    const refs = [];
    collectExprFieldRefs(cond.left, refs);
    collectExprFieldRefs(cond.right, refs);
    if (cond.rightMax)
        collectExprFieldRefs(cond.rightMax, refs);
    if (cond.rightList) {
        for (const e of cond.rightList)
            collectExprFieldRefs(e, refs);
    }
    return refs;
}
function hasCycle(graph, startId) {
    const visited = new Set();
    const onStack = new Set();
    const dfs = (nodeId) => {
        if (onStack.has(nodeId))
            return true;
        if (visited.has(nodeId))
            return false;
        visited.add(nodeId);
        onStack.add(nodeId);
        for (const edge of getOutgoingEdges(graph, nodeId)) {
            if (dfs(edge.target))
                return true;
        }
        onStack.delete(nodeId);
        return false;
    };
    return dfs(startId);
}
